import { Emitter } from './utils/emitter';
import { isOriginAllowed, getCheckoutOrigin, getApiOrigin, getVaultApiUrl } from './config';
import { ThreeDSModalController, type ThreeDSFlowData } from './threeds-modal';
import { WalletOption } from './types';
import {
  ExpressSdkEvent,
  type ExpressButtonOptions,
  type ExpressCheckoutOptions,
  type ExpressCheckoutInstance,
  type ExpressCheckoutStatic,
  type ExpressCheckoutUpdateOptions,
  type ExpressCheckoutShippingOption,
  type ExpressEventName,
  normalizeApplePayLocale,
  normalizeGPayLocale,
} from './types';
import {
  generateId,
  formatDecimalMinorAmount,
  parseDecimalMajorAmount,
  isLiveKey,
  loadApplePayScript,
  loadGooglePayScript,
  buildGpayPaymentDataUpdate,
} from './express-utils';
import { selectApplePaySessionVersion } from './apple-pay-policy';
import { ThreeDSecureInitialization } from './types/express';

/** Merchant callback and layout policy for the legacy Express Checkout surface. */
const EXPRESS_RUNTIME_POLICY = {
  automaticColumnBreakpointCssPixels: 540,
  merchantSessionStartTimeoutMs: 3_000,
  merchantWalletUpdateTimeoutMs: 30_000,
} as const;

// Apple Pay uses 'check-out' (hyphenated); our public API uses 'checkout' to match Google Pay
function toApplePayButtonType(type: string): string {
  return type === 'checkout' ? 'check-out' : type;
}

// Implementation

class ExpressCheckoutImpl implements ExpressCheckoutInstance {
  private emitter = new Emitter<ExpressEventName>();
  private mounted = false;
  private destroyed = false;
  /** True while a wallet payment sheet is open — prevents concurrent sessions */
  private sessionActive = false;
  private messageHandler: (evt: MessageEvent) => void;

  // DOM elements created by mount()
  private tokenizeIframe: HTMLIFrameElement | null = null;
  private wrapper: HTMLDivElement | null = null;
  private applePayContainer: HTMLDivElement | null = null;
  private applePayButtonEl: HTMLElement | null = null;
  private gpayContainer: HTMLDivElement | null = null;
  private gpayClient: any = null;
  private gpayPaymentRequest: any = null;
  private gpayCurrentAmount: number = 0;

  // Layout state
  private preferredLayout: 'row' | 'column' = 'row';
  private layoutResizeObserver: ResizeObserver | null = null;
  private layoutResizeFrame: number | null = null;
  private pendingObservedLayout: 'row' | 'column' | null = null;

  /**
   * Client-generated ID used to route postMessages to the correct instance.
   * Passed to each iframe as a query param; echoed back in every message.
   */
  private readonly instanceId: string = generateId();

  /**
   * Idempotency key for the current payment attempt.
   * Regenerated on each wallet button click so retries get a fresh key.
   */
  private idempotencyKey: string = generateId();

  private threeDSModal: ThreeDSModalController;
  // Pending 3DS data captured during THREEDS_START_FLOW while GPay sheet is still closing.
  // Shown after loadPaymentData() resolves
  private pending3DSAfterGpay: any = null;

  // Apple Pay native session state — session created directly in the SDK window
  // on the merchant's registered domain.
  private applePaySession: any = null;
  private applePayCurrentAmount: number = 0;
  private currentLineItems: any[] = [];
  private applePayCurrentShippingOptions: ExpressCheckoutShippingOption[] = [];
  private applePayPendingShippingOption: ExpressCheckoutShippingOption | null = null;
  private applePayPendingCallback: {
    type: 'contact' | 'method';
    resolve: (update: ExpressCheckoutUpdateOptions) => void;
  } | null = null;
  // Stored from onpaymentauthorized — used to populate PaymentMethodCreated after TOKENIZE_RESPONSE
  private applePayPendingBillingDetails: any = null;
  private applePayPendingShippingDetails: any = null;

  /**
   * Config fetched via HTTP from /api/express/config on mount().
   * Populated before any wallet buttons are mounted.
   */
  private expressConfig: {
    tenantId: string;
    publicKey: string;
    vaultApiUrl: string;
    googlePayMerchantId: string;
    googlePayStatus: string;
    displayName: string;
    applePayStatus: string;
    merchantMid: string;
    merchantMcc: string;
    merchantCountry: string;
    merchantUrl: string;
    threeDSecure: ThreeDSecureInitialization | null;
  } | null = null;

  // Pending Google Pay payment data waiting for tokenization response
  private pendingGpayResolve: ((token: any) => void) | null = null;
  private pendingGpayReject: ((err: Error) => void) | null = null;
  // Stored GPay metadata for use when TOKENIZE_RESPONSE arrives after 3DS (sheet already closed)
  private pendingGpayBillingDetails: any = null;
  private pendingGpayShippingDetails: any = null;
  private pendingGpayShippingOption: ExpressCheckoutShippingOption | undefined = undefined;

  /**
   * Mirrors the current shipping options so Google Pay can look up the amount
   * for a selected shipping option (Google Pay only gives us id + label, not amount).
   * Updated whenever the merchant calls updateWith({ shippingOptions: [...] }).
   */
  private currentShippingOptions: ExpressCheckoutShippingOption[];

  constructor(private options: ExpressCheckoutOptions) {
    this.currentShippingOptions = options.shippingOptions ?? [];
    this.messageHandler = this.onMessage.bind(this);
    this.threeDSModal = new ThreeDSModalController({
      modalId: 'paypercut-express-3ds-modal',
      buildUrl: (data) => this.buildThreeDSUrl(data),
      onCancel: () => this.handle3DSShellCancel(),
      onReady: (iframe) => this.postThreeDSInit(iframe),
    });
    window.addEventListener('message', this.messageHandler);
  }

  // ─── URL builders

  private buildTokenizeSrc(): string {
    const baseUrl = getCheckoutOrigin(this.options.hostedCheckoutUrl);
    const url = new URL('/express/tokenize', baseUrl);
    url.searchParams.set('instanceId', this.instanceId);
    url.searchParams.set('liveMode', String(isLiveKey(this.options.publishableKey)));
    return url.toString();
  }

  // ─── Config fetch

  private async fetchExpressConfig(): Promise<void> {
    const apiOrigin = getApiOrigin(this.options.apiUrl);
    const url = new URL('/v1/elements/sessions', apiOrigin);
    const { amount } = this.options;

    url.searchParams.set('type', 'deferred_intent');
    url.searchParams.set('key', this.options.publishableKey);
    url.searchParams.set('deferred_intent[mode]', 'payment');
    url.searchParams.set('deferred_intent[currency]', this.options.currency);
    if (amount != null) {
      url.searchParams.set('deferred_intent[amount]', `${amount}`);
    }
    url.searchParams.set('referrer_host', window.location.hostname);

    try {
      const res = await fetch(url.toString(), { cache: 'no-store' });
      if (!res.ok) throw new Error(`Config fetch failed: HTTP ${res.status}`);
      const data = await res.json();

      const liveMode = isLiveKey(this.options.publishableKey);
      const googlePayTestMerchantId = 'BCR2DN5T735375Y7';
      const googlePayMerchantRef = data.payment_domain?.wallets?.google_pay?.merchant_ref ?? '';
      const googlePayStatus = data.payment_domain?.wallets?.google_pay?.status ?? 'inactive';

      this.expressConfig = {
        tenantId: data.payment_domain?.wallet_provider?.tenant_id ?? '',
        publicKey: data.payment_domain?.wallet_provider?.public_key ?? '',
        vaultApiUrl: getVaultApiUrl(),
        googlePayMerchantId: liveMode
          ? googlePayStatus === 'active'
            ? googlePayMerchantRef
            : googlePayTestMerchantId
          : googlePayMerchantRef || googlePayTestMerchantId,
        googlePayStatus,
        applePayStatus: data.payment_domain?.wallets?.apple_pay?.status ?? 'inactive',
        displayName: data.merchant?.name ?? '',
        merchantMid: data.merchant?.mid ?? '',
        merchantMcc: data.merchant?.mcc ?? '',
        merchantCountry: data.merchant?.country ?? '',
        merchantUrl: data.merchant?.business_url ?? '',
        threeDSecure: data.three_d_secure ?? null,
      };
    } catch (err) {
      console.error('[PaypercutExpressCheckout] Failed to fetch express config:', err);
      // expressConfig stays null — wallet buttons will not mount
    }
  }

  // postMessage handler

  private onMessage(evt: MessageEvent): void {
    if (!isOriginAllowed(evt.origin)) {
      if (evt.origin.includes('localhost')) {
        console.warn(
          '[PaypercutExpressCheckout] Message blocked — origin not allowed:',
          evt.origin,
        );
      }
      return;
    }

    const data = evt.data;
    if (!data || typeof data !== 'object' || !('type' in data)) return;

    const messageFromTokenizer = Boolean(
      this.tokenizeIframe?.contentWindow && evt.source === this.tokenizeIframe.contentWindow,
    );
    const messageFromThreeDS = this.threeDSModal.ownsMessageSource(evt.source);
    const threeDSFrameMessages = new Set([
      'PAYPERCUT_THREEDS_FRAME_RESIZE',
      'THREEDS_READY',
      'THREEDS_COMPLETE',
      'THREEDS_CANCELED',
      'THREEDS_ERROR',
    ]);
    if (
      (threeDSFrameMessages.has(data.type) && !messageFromThreeDS) ||
      (!threeDSFrameMessages.has(data.type) && !messageFromTokenizer)
    ) {
      return;
    }

    if (data.instanceId && data.instanceId !== this.instanceId) {
      return;
    }

    switch (data.type) {
      // Tokenization response from the hidden tokenize iframe — handles both wallets.
      // For Apple Pay: calls createPaymentMethod, then completes/aborts the Apple Pay sheet.
      // For Google Pay: resolves the pending promise inside handleGooglePayAuthorized.
      case 'TOKENIZE_RESPONSE':
        if ((data as any).wallet === 'apple_pay') {
          if (data.success) {
            this.createPaymentMethod({
              btTokenId: (data as any).btTokenId,
              cardPayload: (data as any).cardPayload,
              billingDetails: (data as any).billingDetails,
              csessId: (data as any).csessId,
              walletType: 'apple_pay',
            })
              .then((paymentMethodId) => {
                this.completeApplePaySession(true);
                this.emitter.emit(ExpressSdkEvent.PaymentMethodCreated, {
                  paymentMethodId,
                  wallet: 'apple_pay',
                  billingDetails: this.applePayPendingBillingDetails ?? undefined,
                  shippingDetails: this.applePayPendingShippingDetails ?? undefined,
                  selectedShippingOption: this.applePayPendingShippingOption ?? undefined,
                });
                this.applePayPendingBillingDetails = null;
                this.applePayPendingShippingDetails = null;
                this.applePayPendingShippingOption = null;
              })
              .catch((err: any) => {
                this.completeApplePaySession(false);
                this.emitter.emit(
                  ExpressSdkEvent.Error,
                  new Error(err?.message ?? 'Payment method creation failed'),
                );
                this.emitter.emit(ExpressSdkEvent.PaymentMethodFailed, {
                  error: err?.message ?? 'Payment method creation failed',
                });
                this.applePayPendingBillingDetails = null;
                this.applePayPendingShippingDetails = null;
                this.applePayPendingShippingOption = null;
              });
          } else {
            this.completeApplePaySession(false);
            this.emitter.emit(
              ExpressSdkEvent.Error,
              new Error((data as any).error ?? 'Apple Pay payment failed'),
            );
            this.emitter.emit(ExpressSdkEvent.PaymentMethodFailed, {
              error: (data as any).error ?? 'Apple Pay payment failed',
            });
            this.applePayPendingBillingDetails = null;
            this.applePayPendingShippingDetails = null;
            this.applePayPendingShippingOption = null;
          }
        } else {
          // Google Pay
          if (data.success) {
            if (this.pendingGpayResolve) {
              // Normal path (no 3DS): resolve and let handleGooglePayAuthorized create PM
              this.pendingGpayResolve(data);
              this.pendingGpayResolve = null;
              this.pendingGpayReject = null;
            } else {
              // Post-3DS path: sheet was already closed — create PM directly, same as Apple Pay
              this.createPaymentMethod({
                btTokenId: (data as any).btTokenId,
                cardPayload: (data as any).cardPayload,
                billingDetails: (data as any).billingDetails,
                csessId: (data as any).csessId,
                walletType: 'google_pay',
              })
                .then((paymentMethodId) => {
                  this.emitter.emit(ExpressSdkEvent.PaymentMethodCreated, {
                    paymentMethodId,
                    wallet: 'google_pay',
                    billingDetails: this.pendingGpayBillingDetails ?? undefined,
                    shippingDetails: this.pendingGpayShippingDetails ?? undefined,
                    selectedShippingOption: this.pendingGpayShippingOption,
                  });
                })
                .catch((err: any) => {
                  this.emitter.emit(
                    ExpressSdkEvent.Error,
                    new Error(err?.message ?? 'Payment method creation failed'),
                  );
                  this.emitter.emit(ExpressSdkEvent.PaymentMethodFailed, {
                    error: err?.message ?? 'Payment method creation failed',
                  });
                })
                .finally(() => {
                  this.pendingGpayBillingDetails = null;
                  this.pendingGpayShippingDetails = null;
                  this.pendingGpayShippingOption = undefined;
                });
            }
          } else {
            if (this.pendingGpayReject) {
              // Normal path (no 3DS): reject the pending promise in handleGooglePayAuthorized
              this.pendingGpayReject(new Error((data as any).error ?? 'Tokenization failed'));
              this.pendingGpayResolve = null;
              this.pendingGpayReject = null;
            } else {
              // Post-3DS path: sheet already closed — emit events directly.
              // Distinguish user cancellation from an actual error so integrators
              // can handle them differently (e.g. "cancel" = re-show checkout,
              // "error" = show an error message).
              const errMsg = (data as any).error ?? 'Google Pay payment failed';
              const isCanceled = errMsg.toLowerCase().includes('cancel');
              if (isCanceled) {
                this.emitter.emit(ExpressSdkEvent.Cancel);
              } else {
                this.emitter.emit(ExpressSdkEvent.Error, new Error(errMsg));
                this.emitter.emit(ExpressSdkEvent.PaymentMethodFailed, { error: errMsg });
              }
              this.pendingGpayBillingDetails = null;
              this.pendingGpayShippingDetails = null;
              this.pendingGpayShippingOption = undefined;
            }
          }
        }
        break;

      // 3DS flow
      case 'PAYPERCUT_THREEDS_FRAME_RESIZE':
        this.threeDSModal.handleFrameResize(evt, data);
        break;

      case 'THREEDS_START_FLOW':
        // Google Pay sheet is a native browser UI that cannot be overlaid by DOM elements.
        // Resolve the pending GPay promise so GPay starts closing its sheet.
        if (this.pendingGpayResolve) {
          this.pending3DSAfterGpay = data;
          this.pendingGpayResolve({ __closeSheet: true });
          this.pendingGpayResolve = null;
          this.pendingGpayReject = null;
        } else {
          // Apple Pay or any path without a pending GPay sheet — show immediately
          this.threeDSModal.show(data);
        }
        break;

      case 'THREEDS_READY':
        this.handle3DSReady();
        break;

      case 'THREEDS_COMPLETE':
      case 'THREEDS_CANCELED':
      case 'THREEDS_ERROR':
        this.postToTokenizeIframe(data);
        this.threeDSModal.close();
        break;
    }
  }

  // ─── Public API ──

  mount(container: HTMLElement | string): void {
    if (this.mounted) {
      console.warn('[PaypercutExpressCheckout] mount() called but already mounted — ignoring');
      return;
    }

    const el =
      typeof container === 'string' ? document.querySelector<HTMLElement>(container) : container;

    if (!el) {
      throw new Error(`[PaypercutExpressCheckout] Container not found: ${container}`);
    }

    const requestedWallets = this.options.walletOptions?.length
      ? (this.options.walletOptions as string[])
      : [WalletOption.APPLE_PAY, WalletOption.GOOGLE_PAY];

    const showApplePay = requestedWallets.includes(WalletOption.APPLE_PAY);
    const showGooglePay = requestedWallets.includes(WalletOption.GOOGLE_PAY);

    // Wrapper — auto (default), row, or column layout; 'auto' uses ResizeObserver
    const layoutOption = this.options.layout ?? 'auto';
    this.preferredLayout = layoutOption === 'auto' ? 'row' : layoutOption;
    const wrapper = document.createElement('div');
    this.wrapper = wrapper;
    Object.assign(wrapper.style, {
      display: 'flex',
      flexDirection: this.preferredLayout === 'row' ? 'row' : 'column',
      gap: '8px',
      width: '100%',
    });

    // Placeholder containers — filled after config is fetched
    // Google Pay is rendered first, Apple Pay second
    if (showGooglePay) {
      this.gpayContainer = document.createElement('div');
      this.applyLayoutToContainers(this.preferredLayout, this.gpayContainer);
      wrapper.appendChild(this.gpayContainer);

      // Preload Google Pay script in parallel while config is being fetched
      loadGooglePayScript().catch((err) => {
        console.error('[PaypercutExpressCheckout] Failed to load Google Pay script:', err);
      });
    }

    if (showApplePay) {
      this.applePayContainer = document.createElement('div');
      this.applyLayoutToContainers(this.preferredLayout, this.applePayContainer);
      wrapper.appendChild(this.applePayContainer);

      // Pre-load Apple Pay JS SDK in parallel while config is being fetched.
      // Resolves even on non-Apple browsers — ApplePaySession check happens after.
      loadApplePayScript();
    }

    el.appendChild(wrapper);

    // Auto layout — observe container width and switch at 540px breakpoint
    if (layoutOption === 'auto' && typeof ResizeObserver !== 'undefined') {
      this.layoutResizeObserver = new ResizeObserver((entries) => {
        const width = entries[0]?.contentRect.width ?? el.offsetWidth;
        this.pendingObservedLayout =
          width <= EXPRESS_RUNTIME_POLICY.automaticColumnBreakpointCssPixels ? 'column' : 'row';
        if (this.layoutResizeFrame !== null) return;
        // ResizeObserver callbacks run during layout delivery. Mutating the
        // observed subtree in that callback can create a browser delivery loop,
        // so apply the latest layout on the following animation frame.
        this.layoutResizeFrame = window.requestAnimationFrame(() => {
          this.layoutResizeFrame = null;
          const layout = this.pendingObservedLayout;
          this.pendingObservedLayout = null;
          if (layout) this.applyLayout(layout);
        });
      });
      this.layoutResizeObserver.observe(el);
    }

    // Hidden tokenize iframe (appended to body, off-screen) — used by both wallets
    const tokenizeSrc = this.buildTokenizeSrc();
    this.tokenizeIframe = document.createElement('iframe');
    this.tokenizeIframe.id = 'paypercut-express-tokenize-iframe';
    this.tokenizeIframe.src = tokenizeSrc;
    this.tokenizeIframe.setAttribute('frameborder', '0');
    this.tokenizeIframe.setAttribute('sandbox', 'allow-scripts allow-forms allow-same-origin');
    Object.assign(this.tokenizeIframe.style, {
      position: 'absolute',
      top: '-9999px',
      left: '-9999px',
      width: '1px',
      height: '1px',
      border: 'none',
      visibility: 'hidden',
    });
    document.body.appendChild(this.tokenizeIframe);

    this.mounted = true;

    // Fetch config then mount wallet buttons
    this.fetchExpressConfig().then(async () => {
      if (this.destroyed) return;

      const config = this.expressConfig;

      if (showApplePay && this.applePayContainer && config?.applePayStatus === 'active') {
        await this.mountApplePayButton(this.applePayContainer);
      } else if (this.applePayContainer) {
        // Apple Pay can't mount — remove its container so the remaining button fills full width
        this.removeApplePayContainer();
      }

      if (showGooglePay && config) {
        this.mountGooglePayFromConfig();
      }

      this.emitter.emit(ExpressSdkEvent.Ready);
    });
  }

  // ─── Layout ──────────────────────────────────────

  private applyLayoutToContainers(
    layout: 'row' | 'column',
    ...containers: (HTMLDivElement | null)[]
  ): void {
    for (const container of containers) {
      if (!container) continue;
      container.style.flex = layout === 'row' ? '1' : '';
      container.style.minWidth = layout === 'row' ? '0' : '';
      container.style.width = layout === 'row' ? '' : '100%';
    }
  }

  private applyLayout(layout: 'row' | 'column'): void {
    if (!this.wrapper) return;
    this.wrapper.style.flexDirection = layout === 'row' ? 'row' : 'column';
    this.applyLayoutToContainers(layout, this.applePayContainer, this.gpayContainer);
  }

  /**
   * Dynamically change the button layout after mount.
   * 'row' places buttons side by side, 'column' stacks them vertically.
   */
  public setLayout(layout: 'row' | 'column'): void {
    this.preferredLayout = layout;
    this.applyLayout(layout);
  }

  /**
   * Dynamically change the locale of the wallet buttons after mount.
   *
   * Pass an ISO 639-1 two-letter language code (e.g. `'en'`, `'bg'`, `'pl'`).
   * Each wallet applies this independently:
   * - Apple Pay updates its button label immediately.
   * - Google Pay recreates its button with the new locale.
   *
   * Unsupported languages fall back to `'en'`. See `ExpressLocale` for the full list.
   *
   * @example
   * express.setLocale('bg'); // switch both buttons to Bulgarian
   */
  public setLocale(locale: string): void {
    this.options.locale = locale;
    if (this.applePayButtonEl) {
      this.applePayButtonEl.setAttribute('locale', normalizeApplePayLocale(locale));
    }
    if (this.gpayClient && this.gpayContainer && this.gpayPaymentRequest) {
      this.setButtonOptions({});
    }
  }

  public setButtonOptions(opts: Partial<ExpressButtonOptions>): void {
    this.options.buttonOptions = { ...this.options.buttonOptions, ...opts };
    const merged = this.options.buttonOptions;

    // ── Apple Pay: update attributes on the live <apple-pay-button> element ──
    if (this.applePayButtonEl) {
      if (opts.applePayStyle !== undefined) {
        this.applePayButtonEl.setAttribute('buttonstyle', opts.applePayStyle);
      }
      if (opts.type !== undefined) {
        this.applePayButtonEl.setAttribute('type', toApplePayButtonType(opts.type));
      }
      if (opts.borderRadius !== undefined) {
        this.applePayButtonEl.style.setProperty(
          '--apple-pay-button-border-radius',
          opts.borderRadius,
        );
      }
    }

    // ── Google Pay: recreate the button (GPay SDK has no update API) ──
    if (this.gpayClient && this.gpayContainer && this.gpayPaymentRequest) {
      this.gpayContainer.innerHTML = '';
      const button = this.gpayClient.createButton({
        onClick: () => this.startGooglePay(this.gpayPaymentRequest),
        buttonSizeMode: 'fill',
        buttonType: merged?.type ?? 'plain',
        buttonColor: merged?.googlePayColor ?? 'default',
        ...(this.options.locale && { buttonLocale: normalizeGPayLocale(this.options.locale) }),
      });
      const borderRadius = merged?.borderRadius ?? '0.5rem';
      button.style.width = '100%';
      this.injectGpayInnerStyles(button, borderRadius);
      this.gpayContainer.appendChild(button);
    }
  }

  // ─── Apple Pay native button ────────────────────

  private async mountApplePayButton(container: HTMLDivElement): Promise<void> {
    await loadApplePayScript();

    const ApplePayAPI = (window as any).ApplePaySession;

    if (!ApplePayAPI) {
      console.log('[PaypercutExpressCheckout] ApplePaySession not available');
      return;
    }

    if (!ApplePayAPI.canMakePayments?.()) {
      console.log(
        '[PaypercutExpressCheckout] ApplePaySession.canMakePayments() returned false — hiding Apple Pay button',
      );
      return;
    }

    const locale = this.options.locale ? normalizeApplePayLocale(this.options.locale) : 'en';
    const buttonOpts = this.options.buttonOptions;
    const button = document.createElement('apple-pay-button');
    button.setAttribute('buttonstyle', buttonOpts?.applePayStyle ?? 'black');
    button.setAttribute('type', toApplePayButtonType(buttonOpts?.type ?? 'plain'));
    button.setAttribute('locale', locale);

    button.style.setProperty('--apple-pay-button-width', '100%');
    button.style.setProperty('--apple-pay-button-height', '48px');
    button.style.setProperty(
      '--apple-pay-button-border-radius',
      buttonOpts?.borderRadius ?? '0.5rem',
    );
    button.style.display = 'block';
    button.style.cursor = 'pointer';

    button.addEventListener('click', () => this.startApplePay());

    // <apple-pay-button> is a custom element registered by the Apple Pay JS SDK.
    // In Safari, ApplePaySession is native so it may already pass the check above
    // before the SDK script has finished registering the custom element.
    // Wait for it to be defined before appending so it renders correctly.
    const appendButton = () => {
      this.applePayButtonEl = button;
      container.appendChild(button);
      const shadow = (button as any).shadowRoot;
      if (shadow) {
        const style = document.createElement('style');
        style.textContent = 'span svg { height: 75% !important; width: auto !important; }';
        shadow.appendChild(style);
      }
    };

    if (customElements?.get('apple-pay-button')) {
      appendButton();
    } else {
      customElements
        ?.whenDefined('apple-pay-button')
        .then(appendButton)
        .catch(() => appendButton());
    }
  }

  /**
   * Starts a native Apple Pay session in the SDK window (merchant's registered domain).
   * Called synchronously from the Apple Pay button click handler.
   * Uses async/await which Safari preserves as part of the user gesture.
   */
  private async startApplePay(): Promise<void> {
    if (this.sessionActive) return;
    if (typeof window === 'undefined') return;

    const ApplePayAPI = (window as any).ApplePaySession;

    if (!ApplePayAPI) {
      console.error('[PaypercutExpressCheckout] ApplePaySession not available');
      return;
    }

    this.idempotencyKey = generateId();
    this.setSessionActive(true);
    this.emitter.emit(ExpressSdkEvent.Click);

    // Emit SessionStart — merchant must call updateWith() within 3s
    const sessionInitResult = await new Promise<ExpressCheckoutUpdateOptions | null>((resolve) => {
      if (!this.emitter.hasListeners(ExpressSdkEvent.SessionStart)) {
        resolve(null);
        return;
      }

      const timer = setTimeout(
        () => resolve(null),
        EXPRESS_RUNTIME_POLICY.merchantSessionStartTimeoutMs,
      );
      this.emitter.emit(ExpressSdkEvent.SessionStart, {
        wallet: 'apple_pay',
        updateWith: (opts: ExpressCheckoutUpdateOptions) => {
          clearTimeout(timer);
          resolve(opts);
        },
      });
    });

    if (!sessionInitResult) {
      console.error('[PaypercutExpressCheckout] Apple Pay: no session_start response — aborting');
      this.setSessionActive(false);
      this.emitter.emit(ExpressSdkEvent.ShippingOptionsRequired, { wallet: 'apple_pay' });
      return;
    }

    // Apply merchant-provided initial state
    if (typeof sessionInitResult.amount === 'number') {
      this.applePayCurrentAmount = sessionInitResult.amount;
    } else {
      this.applePayCurrentAmount = this.options.amount;
    }
    if (Array.isArray(sessionInitResult.lineItems)) {
      this.currentLineItems = sessionInitResult.lineItems;
    } else {
      this.currentLineItems = this.options.lineItems ?? [];
    }
    if (Array.isArray(sessionInitResult.shippingOptions)) {
      this.applePayCurrentShippingOptions = sessionInitResult.shippingOptions;
    } else {
      this.applePayCurrentShippingOptions = this.currentShippingOptions;
    }
    this.applePayPendingShippingOption = null;

    const displayName = this.expressConfig?.displayName || 'Checkout';
    const totalPrice = formatDecimalMinorAmount(this.applePayCurrentAmount);

    const appleLineItems: any[] = this.currentLineItems.map((li) => ({
      label: li.label,
      amount: formatDecimalMinorAmount(li.amount),
      type: li.type ?? 'final',
    }));

    const request: any = {
      countryCode: this.options.countryCode ?? 'DE',
      currencyCode: this.options.currency,
      merchantCapabilities: ['supports3DS', 'supportsCredit', 'supportsDebit'],
      supportedNetworks: ['visa', 'masterCard'],
      lineItems: appleLineItems,
      total: {
        label: displayName,
        amount: totalPrice,
        type: 'final',
      },
      requiredBillingContactFields: ['postalAddress', 'name', 'email'],
    };

    // Shipping is always required for Express Checkout
    request.requiredShippingContactFields = ['postalAddress', 'name', 'email'];
    if (this.applePayCurrentShippingOptions.length) {
      request.shippingMethods = this.applePayCurrentShippingOptions.map((so) => ({
        label: so.label,
        detail: so.detail ?? '',
        amount: formatDecimalMinorAmount(so.amount),
        identifier: so.id,
      }));
    }
    request.shippingType = 'shipping';

    this.abortApplePaySession();

    const version = selectApplePaySessionVersion(ApplePayAPI);

    try {
      const session = new ApplePayAPI(version, request);
      this.applePaySession = session;

      session.onvalidatemerchant = async (event: any) => {
        const vaultApiUrl = this.expressConfig?.vaultApiUrl ?? getVaultApiUrl();
        const publicKey = this.expressConfig?.publicKey ?? '';
        try {
          const headers: Record<string, string> = {
            'Content-Type': 'application/json',
            'BT-API-KEY': publicKey,
          };
          if (this.idempotencyKey) headers['Idempotency-Key'] = this.idempotencyKey;

          const res = await fetch(`${vaultApiUrl}/apple-pay/session`, {
            method: 'POST',
            headers,
            body: JSON.stringify({
              display_name: displayName,
              domain: window.location.host,
              validation_url: event.validationURL,
            }),
          });

          if (!res.ok) {
            const err = await res.json().catch(() => ({}));
            throw new Error(err?.message ?? `Merchant validation failed: HTTP ${res.status}`);
          }

          const merchantSession = await res.json();
          session.completeMerchantValidation(merchantSession);
        } catch (err: any) {
          console.error('[PaypercutExpressCheckout] Apple Pay merchant validation failed');
          this.abortApplePaySession();
          this.emitter.emit(
            ExpressSdkEvent.Error,
            new Error(err?.message ?? 'Apple Pay merchant validation failed'),
          );
          this.emitter.emit(ExpressSdkEvent.Cancel);
        }
      };

      // Shipping address changed — emit to merchant, wait for updateWith(), complete session
      session.onshippingcontactselected = async (event: any) => {
        const contact = event.shippingContact;

        const update = await new Promise<ExpressCheckoutUpdateOptions>((resolve) => {
          this.applePayPendingCallback = { type: 'contact', resolve };
          this.emitter.emit(ExpressSdkEvent.ShippingAddressChange, {
            address: {
              country: contact.countryCode ?? '',
              state: contact.administrativeArea,
              city: contact.locality,
              postalCode: contact.postalCode,
            },
            updateWith: (opts: ExpressCheckoutUpdateOptions) => this.update(opts),
          });
          // 30s safety timeout — return unchanged data so the sheet doesn't freeze
          setTimeout(() => resolve({}), EXPRESS_RUNTIME_POLICY.merchantWalletUpdateTimeoutMs);
        });

        this.applePayPendingCallback = null;

        if (typeof update.amount === 'number') this.applePayCurrentAmount = update.amount;
        if (Array.isArray(update.lineItems)) this.currentLineItems = update.lineItems;
        if (Array.isArray(update.shippingOptions))
          this.applePayCurrentShippingOptions = update.shippingOptions;

        const status =
          update.status === 'invalid_shipping_address'
            ? ApplePayAPI.STATUS_INVALID_SHIPPING_POSTAL_ADDRESS
            : ApplePayAPI.STATUS_SUCCESS;

        try {
          session.completeShippingContactSelection(
            status,
            this.applePayCurrentShippingOptions.map((so) => ({
              label: so.label,
              detail: so.detail ?? '',
              amount: formatDecimalMinorAmount(so.amount),
              identifier: so.id,
            })),
            {
              label: displayName,
              amount: formatDecimalMinorAmount(this.applePayCurrentAmount),
              type: 'final',
            },
            this.currentLineItems.map((li) => ({
              label: li.label,
              amount: formatDecimalMinorAmount(li.amount),
              type: li.type ?? 'final',
            })),
          );
        } catch (e) {
          console.error('[PaypercutExpressCheckout] completeShippingContactSelection failed', e);
        }
      };

      // Shipping method changed — emit to merchant, wait for updateWith(), complete session
      session.onshippingmethodselected = async (event: any) => {
        const method = event.shippingMethod;
        const selectedOption: ExpressCheckoutShippingOption = {
          id: method.identifier,
          label: method.label,
          detail: method.detail,
          amount: parseDecimalMajorAmount(method.amount),
        };
        this.applePayPendingShippingOption = selectedOption;

        const update = await new Promise<ExpressCheckoutUpdateOptions>((resolve) => {
          this.applePayPendingCallback = { type: 'method', resolve };
          this.emitter.emit(ExpressSdkEvent.ShippingOptionChange, {
            shippingOption: selectedOption,
            updateWith: (opts: ExpressCheckoutUpdateOptions) => this.update(opts),
          });
          setTimeout(() => resolve({}), EXPRESS_RUNTIME_POLICY.merchantWalletUpdateTimeoutMs);
        });

        this.applePayPendingCallback = null;

        if (typeof update.amount === 'number') this.applePayCurrentAmount = update.amount;
        if (Array.isArray(update.lineItems)) this.currentLineItems = update.lineItems;

        try {
          session.completeShippingMethodSelection(
            ApplePayAPI.STATUS_SUCCESS,
            {
              label: displayName,
              amount: formatDecimalMinorAmount(this.applePayCurrentAmount),
              type: 'final',
            },
            this.currentLineItems.map((li) => ({
              label: li.label,
              amount: formatDecimalMinorAmount(li.amount),
              type: li.type ?? 'final',
            })),
          );
        } catch (e) {
          console.error('[PaypercutExpressCheckout] completeShippingMethodSelection failed', e);
        }
      };

      // Payment authorized — send raw token to tokenize iframe for BT tokenization
      // + createExpressPaymentMethod. completePayment is called from TOKENIZE_RESPONSE handler.
      session.onpaymentauthorized = (event: any) => {
        const billingContact = event.payment?.billingContact;
        const shippingContact = event.payment?.shippingContact;

        this.applePayPendingBillingDetails = billingContact
          ? {
              name:
                [billingContact.givenName, billingContact.familyName].filter(Boolean).join(' ') ||
                undefined,
              email: billingContact.emailAddress,
              address: {
                line1: billingContact.addressLines?.[0],
                line2: billingContact.addressLines?.[1],
                city: billingContact.locality,
                state: billingContact.administrativeArea,
                postalCode: billingContact.postalCode,
                country: billingContact.countryCode,
              },
            }
          : null;

        this.applePayPendingShippingDetails = shippingContact
          ? {
              name:
                [shippingContact.givenName, shippingContact.familyName].filter(Boolean).join(' ') ||
                undefined,
              address: {
                line1: shippingContact.addressLines?.[0],
                line2: shippingContact.addressLines?.[1],
                city: shippingContact.locality,
                state: shippingContact.administrativeArea,
                postalCode: shippingContact.postalCode,
                country: shippingContact.countryCode,
              },
            }
          : null;

        this.postToTokenizeIframe({
          type: 'TOKENIZE_REQUEST',
          instanceId: this.instanceId,
          wallet: 'apple_pay',
          applePaymentData: event.payment.token,
          idemKey: this.idempotencyKey,
          publishableKey: this.options.publishableKey,
          billingDetails: this.applePayPendingBillingDetails,
          amount: this.applePayCurrentAmount,
          currency: this.options.currency,
          merchantInfo: {
            name: this.expressConfig?.displayName ?? '',
            mid: this.expressConfig?.merchantMid ?? '',
            mcc: this.expressConfig?.merchantMcc ?? '',
            country: this.expressConfig?.merchantCountry ?? '',
            url: this.expressConfig?.merchantUrl ?? '',
          },
          threeDSecure: this.expressConfig?.threeDSecure ?? null,
        });
      };

      session.oncancel = () => {
        this.applePaySession = null;
        this.applePayPendingCallback = null;
        this.applePayPendingShippingOption = null;
        this.emitter.emit(ExpressSdkEvent.Cancel);
      };

      session.begin();
    } catch (e: any) {
      this.applePaySession = null;
      console.error('[PaypercutExpressCheckout] Failed to create ApplePaySession', {
        name: e?.name,
        message: e?.message,
        hint:
          e?.name === 'SecurityError'
            ? 'Page is not HTTPS or merchant domain is not registered for Apple Pay'
            : 'Check isSecureContext and that ApplePaySession.canMakePayments() returns true',
      });
    }
  }

  // ─── Google Pay native button ───────────────────

  /**
   * Called after config is fetched. Waits for the GPay script to finish loading,
   * then mounts the button.
   */
  private removeGpayContainer(): void {
    this.gpayContainer?.remove();
    this.gpayContainer = null;
  }

  private removeApplePayContainer(): void {
    this.applePayContainer?.remove();
    this.applePayContainer = null;
    this.applePayButtonEl = null;
  }

  private mountGooglePayFromConfig(): void {
    const config = this.expressConfig;
    if (!config) return;

    const liveMode = isLiveKey(this.options.publishableKey);
    const tenantId = config.tenantId;
    const googlePayMerchantId = config.googlePayMerchantId;
    const merchantName = config.displayName || 'Checkout';

    if (!tenantId) {
      console.error(
        '[PaypercutExpressCheckout] tenantId is empty — Google Pay cannot be mounted without a valid gatewayMerchantId.',
      );
      this.removeGpayContainer();
      return;
    }

    if (!googlePayMerchantId) {
      console.warn(
        '[PaypercutExpressCheckout] googlePayMerchantId is empty — Google Pay will not be mounted (merchant may not have Google Pay active).',
      );
      this.removeGpayContainer();
      return;
    }

    console.log({ liveMode });
    if (liveMode && (!config.merchantMcc || !config.merchantUrl)) {
      console.warn('[PaypercutExpressCheckout] Google Pay disabled!');
      this.removeGpayContainer();
      return;
    }

    loadGooglePayScript()
      .then(() => {
        this.mountGooglePayButton(tenantId, googlePayMerchantId, merchantName, liveMode);
      })
      .catch((err) => {
        console.error('[PaypercutExpressCheckout] Failed to load Google Pay script:', err);
      });
  }

  private mountGooglePayButton(
    tenantId: string,
    merchantId: string,
    merchantName: string,
    liveMode: boolean,
  ): void {
    if (!this.gpayContainer) {
      console.error('[PaypercutExpressCheckout] gpayContainer is null');
      return;
    }

    const google = (globalThis as any).google;
    if (!google?.payments?.api?.PaymentsClient) {
      console.error('[PaypercutExpressCheckout] google.payments.api.PaymentsClient not available');
      return;
    }

    const environment = liveMode ? 'PRODUCTION' : 'TEST';
    const requestShipping = true;
    const countryCode = this.options.countryCode;
    if (!countryCode) {
      console.warn(
        '[PaypercutExpressCheckout] countryCode is not set. This is required for Google Pay in production. Pass it via options.countryCode (e.g. "DE").',
      );
    }

    const paymentRequest: any = {
      apiVersion: 2,
      apiVersionMinor: 0,
      allowedPaymentMethods: [
        {
          type: 'CARD',
          parameters: {
            allowedAuthMethods: ['CRYPTOGRAM_3DS', 'PAN_ONLY'],
            allowedCardNetworks: ['MASTERCARD', 'VISA'],
            billingAddressRequired: true,
            billingAddressParameters: {
              format: 'FULL',
              phoneNumberRequired: false,
            },
          },
          tokenizationSpecification: {
            type: 'PAYMENT_GATEWAY',
            parameters: {
              gateway: 'basistheory',
              gatewayMerchantId: tenantId,
            },
          },
        },
      ],
      merchantInfo: {
        merchantId,
        merchantName,
      },
      transactionInfo: {
        totalPriceStatus: 'FINAL',
        totalPriceLabel: 'Total',
        totalPrice: formatDecimalMinorAmount(this.options.amount),
        currencyCode: this.options.currency,
        countryCode,
      },
      callbackIntents: requestShipping
        ? ['PAYMENT_AUTHORIZATION', 'SHIPPING_ADDRESS', 'SHIPPING_OPTION']
        : ['PAYMENT_AUTHORIZATION'],
    };

    if (requestShipping) {
      paymentRequest.shippingAddressRequired = true;
      paymentRequest.shippingOptionRequired = true;
      if (this.options.allowedCountries?.length) {
        paymentRequest.shippingAddressParameters = {
          allowedCountryCodes: this.options.allowedCountries,
          phoneNumberRequired: false,
        };
      }
      if (this.options.shippingOptions?.length) {
        paymentRequest.shippingOptionParameters = {
          defaultSelectedOptionId: this.options.shippingOptions[0].id,
          shippingOptions: this.options.shippingOptions.map((so) => ({
            id: so.id,
            label: so.label,
            description:
              so.detail ?? `${formatDecimalMinorAmount(so.amount)} ${this.options.currency}`,
          })),
        };
      }
    }

    this.gpayPaymentRequest = paymentRequest;
    this.gpayClient = new google.payments.api.PaymentsClient({
      environment,
      paymentDataCallbacks: {
        onPaymentAuthorized: (paymentData: any) => this.handleGooglePayAuthorized(paymentData),
        ...(requestShipping && {
          onPaymentDataChanged: (intermediatePaymentData: any) =>
            this.handleGooglePayDataChanged(intermediatePaymentData),
        }),
      },
    });

    const buttonOpts = this.options.buttonOptions;
    const button = this.gpayClient.createButton({
      onClick: () => this.startGooglePay(paymentRequest),
      buttonSizeMode: 'fill',
      buttonType: buttonOpts?.type ?? 'plain',
      buttonColor: buttonOpts?.googlePayColor ?? 'default',
      ...(this.options.locale && { buttonLocale: normalizeGPayLocale(this.options.locale) }),
    });

    const borderRadius = buttonOpts?.borderRadius ?? '0.5rem';
    button.style.width = '100%';
    this.injectGpayInnerStyles(button, borderRadius);
    this.gpayContainer.appendChild(button);
  }

  private injectGpayInnerStyles(buttonEl: HTMLElement, borderRadius: string): void {
    const style = document.createElement('style');
    style.textContent = `.gpay-button { height: 48px !important; border-radius: ${borderRadius} !important; background-size: auto 60% !important; } .gpay-card-info-container { border-radius: ${borderRadius} !important; } .gpay-card-info-container-fill, .gpay-card-info-container-fill > .gpay-card-info-container { height: 48px !important; }`;
    buttonEl.prepend(style);
  }

  private async startGooglePay(paymentRequest: any): Promise<void> {
    if (!this.gpayClient) return;
    if (this.sessionActive) return;
    this.idempotencyKey = generateId();
    this.setSessionActive(true);
    this.emitter.emit(ExpressSdkEvent.Click);

    // Ask merchant for initial shipping options before opening the sheet.
    // If no session_start handler is registered, skip straight through.
    if (this.emitter.hasListeners(ExpressSdkEvent.SessionStart)) {
      const resolved = await new Promise<ExpressCheckoutUpdateOptions | null>((resolve) => {
        const timer = setTimeout(
          () => resolve(null),
          EXPRESS_RUNTIME_POLICY.merchantSessionStartTimeoutMs,
        );
        this.emitter.emit(ExpressSdkEvent.SessionStart, {
          wallet: 'google_pay',
          updateWith: (opts: ExpressCheckoutUpdateOptions) => {
            clearTimeout(timer);
            resolve(opts);
          },
        });
      });

      if (!resolved) {
        // Merchant didn't respond — do not open the sheet
        this.setSessionActive(false);
        this.emitter.emit(ExpressSdkEvent.ShippingOptionsRequired, { wallet: 'google_pay' });
        return;
      }

      // Apply merchant's options before loadPaymentData
      if (resolved.shippingOptions) {
        this.currentShippingOptions = resolved.shippingOptions;
        paymentRequest.shippingOptionParameters = {
          defaultSelectedOptionId: resolved.shippingOptions[0]?.id,
          shippingOptions: resolved.shippingOptions.map((so) => ({
            id: so.id,
            label: so.label,
            description:
              so.detail ?? `${formatDecimalMinorAmount(so.amount)} ${this.options.currency}`,
          })),
        };
      }
      if (resolved.amount != null) {
        this.gpayCurrentAmount = resolved.amount;
        paymentRequest.transactionInfo = {
          ...paymentRequest.transactionInfo,
          totalPrice: formatDecimalMinorAmount(resolved.amount),
        };
      }
    }

    // Ensure gpayCurrentAmount is always set before the sheet opens
    if (!this.gpayCurrentAmount) {
      this.gpayCurrentAmount = this.options.amount;
    }

    try {
      await this.gpayClient.loadPaymentData(paymentRequest);
      // If 3DS was triggered, the GPay sheet is now fully dismissed — show the challenge modal.
      if (this.pending3DSAfterGpay) {
        const threeDSData = this.pending3DSAfterGpay;
        this.pending3DSAfterGpay = null;
        this.threeDSModal.show(threeDSData);
      }
    } catch (e: any) {
      if (e?.statusCode === 'CANCELED' || e?.message?.toLowerCase?.().includes('cancel')) {
        this.emitter.emit(ExpressSdkEvent.Cancel);
      } else {
        this.emitter.emit(ExpressSdkEvent.Error, new Error(e?.message ?? 'Google Pay failed'));
      }
    } finally {
      this.setSessionActive(false);
    }
  }

  private async handleGooglePayAuthorized(paymentData: any): Promise<{ transactionState: string }> {
    try {
      const encryptedToken: string = paymentData.paymentMethodData.tokenizationData.token;
      const cardNetwork: string = paymentData.paymentMethodData?.info?.cardNetwork ?? '';
      const billingAddr = paymentData.paymentMethodData?.info?.billingAddress;
      const shippingAddr = paymentData.shippingAddress;
      const shippingOptionData = paymentData.shippingOptionData;

      const billingDetails = billingAddr
        ? {
            name: billingAddr.name,
            address: {
              line1: billingAddr.address1,
              city: billingAddr.locality,
              state: billingAddr.administrativeArea,
              postalCode: billingAddr.postalCode,
              country: billingAddr.countryCode,
            },
          }
        : null;

      const shippingDetails = shippingAddr
        ? {
            name: shippingAddr.name || undefined,
            address: {
              line1: shippingAddr.address1,
              city: shippingAddr.locality,
              state: shippingAddr.administrativeArea,
              postalCode: shippingAddr.postalCode,
              country: shippingAddr.countryCode,
            },
          }
        : undefined;

      const selectedShippingOption: ExpressCheckoutShippingOption | undefined = shippingOptionData
        ? (this.currentShippingOptions.find((so) => so.id === shippingOptionData.id) ?? {
            id: shippingOptionData.id,
            label: shippingOptionData.label ?? shippingOptionData.id,
            amount: 0,
          })
        : undefined;

      // Store metadata upfront — needed if 3DS triggers and the sheet closes before TOKENIZE_RESPONSE
      this.pendingGpayBillingDetails = billingDetails;
      this.pendingGpayShippingDetails = shippingDetails;
      this.pendingGpayShippingOption = selectedShippingOption;

      // Send to hidden tokenize iframe — it will BT-tokenize + create the payment method
      const tokenResult = await new Promise<any>((resolve, reject) => {
        const timeoutId = setTimeout(() => {
          this.pendingGpayResolve = null;
          this.pendingGpayReject = null;
          reject(new Error('Tokenization timed out'));
        }, EXPRESS_RUNTIME_POLICY.merchantWalletUpdateTimeoutMs);

        this.pendingGpayResolve = (result) => {
          clearTimeout(timeoutId);
          resolve(result);
        };
        this.pendingGpayReject = (err) => {
          clearTimeout(timeoutId);
          reject(err);
        };

        this.postToTokenizeIframe({
          type: 'TOKENIZE_REQUEST',
          instanceId: this.instanceId,
          wallet: 'google_pay',
          encryptedToken,
          cardNetwork,
          idemKey: this.idempotencyKey,
          publishableKey: this.options.publishableKey,
          billingDetails,
          amount: this.options.amount,
          currency: this.options.currency,
          merchantInfo: {
            name: this.expressConfig?.displayName ?? '',
            mid: this.expressConfig?.merchantMid ?? '',
            mcc: this.expressConfig?.merchantMcc ?? '',
            country: this.expressConfig?.merchantCountry ?? '',
            url: this.expressConfig?.merchantUrl ?? '',
          },
          threeDSecure: this.expressConfig?.threeDSecure ?? null,
        });
      });

      // 3DS was triggered — sheet already closed, payment method creation handled in message handler
      if (tokenResult.__closeSheet) {
        return { transactionState: 'SUCCESS' };
      }

      if (!tokenResult.success) {
        const errMsg = tokenResult.error ?? 'Tokenization failed';
        this.emitter.emit(ExpressSdkEvent.Error, new Error(errMsg));
        this.emitter.emit(ExpressSdkEvent.PaymentMethodFailed, { error: errMsg });
        return { transactionState: 'ERROR' };
      }

      const paymentMethodId = await this.createPaymentMethod({
        btTokenId: tokenResult.btTokenId,
        cardPayload: tokenResult.cardPayload,
        billingDetails: tokenResult.billingDetails,
        csessId: tokenResult.csessId,
        walletType: 'google_pay',
      });

      this.emitter.emit(ExpressSdkEvent.PaymentMethodCreated, {
        paymentMethodId,
        wallet: 'google_pay',
        billingDetails: billingDetails ?? undefined,
        shippingDetails,
        selectedShippingOption,
      });

      this.pendingGpayBillingDetails = null;
      this.pendingGpayShippingDetails = null;
      this.pendingGpayShippingOption = undefined;

      return { transactionState: 'SUCCESS' };
    } catch (err: any) {
      console.error(
        '[PaypercutExpressCheckout] handleGooglePayAuthorized error:',
        err?.message ?? err,
      );
      this.emitter.emit(
        ExpressSdkEvent.Error,
        new Error(err?.message ?? 'Google Pay authorization failed'),
      );
      this.emitter.emit(ExpressSdkEvent.PaymentMethodFailed, {
        error: err?.message ?? 'Google Pay authorization failed',
      });
      return { transactionState: 'ERROR' };
    }
  }

  /**
   * Google Pay `onPaymentDataChanged` callback.
   * Called when the user changes their shipping address or shipping option.
   */
  private async handleGooglePayDataChanged(intermediatePaymentData: any): Promise<object> {
    const { callbackTrigger, shippingAddress, shippingOptionData } = intermediatePaymentData;

    if (callbackTrigger === 'INITIALIZE') {
      return buildGpayPaymentDataUpdate(
        {
          amount: this.gpayCurrentAmount || this.options.amount,
          shippingOptions: this.currentShippingOptions,
        },
        this.options.currency,
        this.options.countryCode,
      );
    }

    if (callbackTrigger === 'SHIPPING_ADDRESS') {
      const update = await new Promise<ExpressCheckoutUpdateOptions>((resolve) => {
        this.emitter.emit(ExpressSdkEvent.ShippingAddressChange, {
          address: {
            country: shippingAddress?.countryCode ?? '',
            state: shippingAddress?.administrativeArea,
            city: shippingAddress?.locality,
            postalCode: shippingAddress?.postalCode,
          },
          updateWith: (opts: ExpressCheckoutUpdateOptions) => {
            if (opts.shippingOptions) this.currentShippingOptions = opts.shippingOptions;
            if (opts.amount != null) this.gpayCurrentAmount = opts.amount;
            resolve(opts);
          },
        });
      });

      return buildGpayPaymentDataUpdate(update, this.options.currency, this.options.countryCode);
    }

    if (callbackTrigger === 'SHIPPING_OPTION') {
      const selectedId = shippingOptionData?.id as string | undefined;
      const selected = this.currentShippingOptions.find((so) => so.id === selectedId);

      const update = await new Promise<ExpressCheckoutUpdateOptions>((resolve) => {
        this.emitter.emit(ExpressSdkEvent.ShippingOptionChange, {
          shippingOption: {
            id: selectedId ?? '',
            label: selected?.label ?? selectedId ?? '',
            detail: selected?.detail,
            amount: selected?.amount ?? 0,
          },
          updateWith: (opts: ExpressCheckoutUpdateOptions) => {
            if (opts.amount != null) this.gpayCurrentAmount = opts.amount;
            resolve(opts);
          },
        });
      });

      return buildGpayPaymentDataUpdate(update, this.options.currency, this.options.countryCode);
    }

    return {};
  }

  update(options: ExpressCheckoutUpdateOptions): void {
    if (!this.mounted) {
      console.warn('[PaypercutExpressCheckout] Cannot update: not mounted');
      return;
    }

    // If an Apple Pay shipping callback is waiting, resolve it directly
    if (this.applePayPendingCallback) {
      this.applePayPendingCallback.resolve(options);
      return;
    }
  }

  on(event: ExpressEventName, handler: (...args: any[]) => void): () => void {
    return this.emitter.on(event, handler);
  }

  destroy(): void {
    if (this.destroyed) return;

    if (this.tokenizeIframe) {
      try {
        this.tokenizeIframe.remove();
      } catch {}
      this.tokenizeIframe = null;
    }

    if (this.gpayContainer) {
      try {
        this.gpayContainer.remove();
      } catch {}
      this.gpayContainer = null;
    }
    if (this.applePayContainer) {
      try {
        this.removeApplePayContainer();
      } catch {}
    }
    this.gpayPaymentRequest = null;

    if (this.layoutResizeObserver) {
      this.layoutResizeObserver.disconnect();
      this.layoutResizeObserver = null;
    }
    if (this.layoutResizeFrame !== null) {
      window.cancelAnimationFrame(this.layoutResizeFrame);
      this.layoutResizeFrame = null;
    }
    this.pendingObservedLayout = null;

    this.threeDSModal.close();
    this.abortApplePaySession();
    this.sessionActive = false;
    this.mounted = false;
    this.destroyed = true;
    window.removeEventListener('message', this.messageHandler);
    this.emitter.clear();
  }

  // Private helpers

  private setSessionActive(active: boolean): void {
    this.sessionActive = active;
    const opacity = active ? '0.5' : '';
    const pointerEvents = active ? 'none' : '';
    if (this.applePayContainer) {
      this.applePayContainer.style.opacity = opacity;
      this.applePayContainer.style.pointerEvents = pointerEvents;
      this.applePayContainer.inert = active;
      this.applePayContainer.setAttribute('aria-busy', String(active));
    }
    if (this.gpayContainer) {
      this.gpayContainer.style.opacity = opacity;
      this.gpayContainer.style.pointerEvents = pointerEvents;
      this.gpayContainer.inert = active;
      this.gpayContainer.setAttribute('aria-busy', String(active));
    }
  }

  private completeApplePaySession(success: boolean): void {
    try {
      if (this.applePaySession) {
        const ApplePayAPI = (window as any).ApplePaySession;
        const status = success ? ApplePayAPI.STATUS_SUCCESS : ApplePayAPI.STATUS_FAILURE;
        this.applePaySession.completePayment(status);
      }
    } catch {}
    this.applePaySession = null;
    this.applePayPendingCallback = null;
    this.applePayPendingShippingOption = null;
    this.applePayPendingBillingDetails = null;
    this.applePayPendingShippingDetails = null;
    this.setSessionActive(false);
  }

  private abortApplePaySession(): void {
    try {
      this.applePaySession?.abort();
    } catch {}
    this.applePaySession = null;
    this.applePayPendingCallback = null;
    this.applePayPendingShippingOption = null;
    this.applePayPendingBillingDetails = null;
    this.applePayPendingShippingDetails = null;
    this.setSessionActive(false);
  }

  private async createPaymentMethod(params: {
    btTokenId: string;
    cardPayload: any;
    billingDetails: any;
    csessId?: string | null;
    walletType: 'apple_pay' | 'google_pay';
  }): Promise<string> {
    const { btTokenId, cardPayload, billingDetails, csessId, walletType } = params;

    if (!cardPayload || !btTokenId) {
      throw new Error(`[createPaymentMethod] Missing required tokenization data.`);
    }

    const url = new URL('/v1/payment_methods', getApiOrigin(this.options.apiUrl));

    const body = {
      type: 'card',
      key: this.options.publishableKey,
      card: {
        token: csessId ?? '',
        brand: cardPayload.brand ?? '',
        exp_month: cardPayload.exp_month ?? 1,
        exp_year: cardPayload.exp_year ?? 1,
        funding: cardPayload.funding ?? '',
        last4: cardPayload.last4 ?? '',
        iin: cardPayload.bin ?? '',
        issuer: cardPayload.issuer ?? null,
        fingerprint: cardPayload.fingerprint ?? null,
        segment: cardPayload.segment ?? '',
        wallet: { type: walletType, dynamic_last4: null },
        three_d_secure_usage: { supported: true },
      },
      billing_details: {
        name: billingDetails?.name ?? null,
        email: billingDetails?.email ?? null,
        phone: billingDetails?.phone ?? null,
        address: {
          city: billingDetails?.address?.city ?? null,
          country: billingDetails?.address?.country ?? null,
          line1: billingDetails?.address?.line1 ?? null,
          line2: billingDetails?.address?.line2 ?? null,
          postal_code: billingDetails?.address?.postalCode ?? null,
          state: billingDetails?.address?.state ?? null,
        },
      },
      confirmation_token: { token_id: btTokenId },
    };

    const res = await fetch(url.toString(), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
      cache: 'no-store',
    });

    if (!res.ok) {
      const msg = await res.text().catch(() => `HTTP ${res.status}`);
      throw new Error(`Payment method creation failed (${res.status}): ${msg}`);
    }

    const pm = await res.json();
    return pm.id;
  }

  private postToTokenizeIframe(message: object): void {
    if (!this.tokenizeIframe?.contentWindow) {
      return;
    }

    const origin = getCheckoutOrigin(this.options.hostedCheckoutUrl);
    this.tokenizeIframe.contentWindow.postMessage(message, origin);
  }

  // ─── 3DS modal ──────────

  private buildThreeDSUrl(data: ThreeDSFlowData): string {
    const checkoutOrigin = getCheckoutOrigin(this.options.hostedCheckoutUrl);

    const threeDSUrl = new URL('/express/threeds', checkoutOrigin);
    threeDSUrl.searchParams.set('instanceId', this.instanceId);
    threeDSUrl.searchParams.set('sessionId', data.sessionId);
    threeDSUrl.searchParams.set('step', data.step);
    if (data.challengeUrl) threeDSUrl.searchParams.set('challengeUrl', data.challengeUrl);
    if (data.acsTransactionId)
      threeDSUrl.searchParams.set('acsTransactionId', data.acsTransactionId);
    if (data.threeDSVersion) threeDSUrl.searchParams.set('threeDSVersion', data.threeDSVersion);
    threeDSUrl.searchParams.set('liveMode', String(data.liveMode ?? false));

    return threeDSUrl.toString();
  }

  private handle3DSShellCancel(): void {
    this.postToTokenizeIframe({
      type: 'THREEDS_CANCELED',
      instanceId: this.instanceId,
      error: 'User canceled 3DS authentication',
    });
    this.threeDSModal.close();
    this.emitter.emit(ExpressSdkEvent.Cancel);
  }

  private handle3DSReady(): void {
    if (!this.threeDSModal.handleReady()) {
      console.error('[PaypercutExpressCheckout] No pending 3DS data or iframe not ready');
    }
  }

  private postThreeDSInit(iframe: HTMLIFrameElement): void {
    const origin = getCheckoutOrigin(this.options.hostedCheckoutUrl);
    iframe.contentWindow?.postMessage(
      { type: 'THREEDS_INIT', instanceId: this.instanceId },
      origin,
    );
  }
}

//Factory

const PaypercutExpressCheckout: ExpressCheckoutStatic = function (
  this: unknown,
  options: ExpressCheckoutOptions,
): ExpressCheckoutInstance {
  return new ExpressCheckoutImpl(options);
} as unknown as ExpressCheckoutStatic;

export default PaypercutExpressCheckout;
export { PaypercutExpressCheckout };
